import { useCallback, useEffect, useMemo, useState } from 'react';
import { PopupProps } from 'react-popup-manager';
import { useL1LocationsQuery } from 'modules/settings/pods/settings-user-location-assignment/service/user-locations/hooks/use-l1-locations.hook';
import { useReplaceUserLocations } from 'modules/settings/pods/settings-user-location-assignment/service/user-locations/hooks/use-replace-user-locations.hook';
import { useUserLocationsQuery } from 'modules/settings/pods/settings-user-location-assignment/service/user-locations/hooks/use-user-locations.hook';
import { hasSameLocations } from './restrict-locations.business';
import { RestrictLocationsComponent } from './restrict-locations.component';

export interface RestrictLocationsTarget {
    id: string;
    fullName: string;
}

export interface RestrictLocationsContainerProps extends PopupProps {
    users: RestrictLocationsTarget[];
    onAssignmentSaved: VoidFunction;
}

export const RestrictLocationsContainer: React.FC<RestrictLocationsContainerProps> = ({
    isOpen = true,
    users,
    onClose,
    onAssignmentSaved,
}) => {
    const isSingleUser = users.length === 1;
    const userIds = useMemo(() => users.map((user) => user.id), [users]);
    const userNames = useMemo(() => users.map((user) => user.fullName), [users]);

    // Only a single target has one current set to show. For several users the dialog starts empty and
    // whatever is picked replaces every selected user's assignment.
    const {
        assignedLocationIds,
        isLoading: isLoadingUser,
        isError: isUserError,
    } = useUserLocationsQuery(isSingleUser ? users[0].id : '');
    const { locations, isLoading: isLoadingLocations, isError: isLocationsError } = useL1LocationsQuery();

    const [draft, setDraft] = useState<string[]>([]);
    const [isSeeded, setIsSeeded] = useState(!isSingleUser);
    const [hasEdited, setHasEdited] = useState(false);

    /** Seeding must not count as an edit, so only the user-driven paths go through here. */
    const editDraft = useCallback((next: string[]) => {
        setHasEdited(true);
        setDraft(next);
    }, []);

    // Seeded once. A later refetch — the conflict comparison in particular — must not overwrite a
    // selection the admin has already made.
    useEffect(() => {
        if (!isSeeded && !isLoadingUser && !isUserError) {
            setDraft(assignedLocationIds);
            setIsSeeded(true);
        }
    }, [isSeeded, isLoadingUser, isUserError, assignedLocationIds]);

    const { save, isSaving } = useReplaceUserLocations({
        userIds,
        onSaved: () => {
            onAssignmentSaved();
            onClose?.();
        },
    });

    const isError = (isSingleUser && isUserError) || isLocationsError;
    // Seeding runs after paint, so an unseeded draft still reads as the empty set: without this the
    // dialog shows "No restricted locations" checked for one frame. A failed read never seeds, hence
    // the isError guard — otherwise the dialog would spin instead of showing the error.
    const isLoading = (isSingleUser && isLoadingUser) || isLoadingLocations || (!isSeeded && !isError);
    const isUnchanged = useMemo(() => hasSameLocations(draft, assignedLocationIds), [draft, assignedLocationIds]);

    // No shared "current" set to diff against, so several users gate on a deliberate edit instead —
    // otherwise Update is live on open, one click from clearing everyone.
    const canSave = isSingleUser ? !isUnchanged : hasEdited;

    const isSelectionKnown = isSingleUser || hasEdited;

    return (
        <RestrictLocationsComponent
            isOpen={isOpen}
            userNames={userNames}
            locations={locations}
            selectedIds={draft}
            isSelectionKnown={isSelectionKnown}
            isLoading={isLoading}
            isError={isError}
            isSaving={isSaving}
            canUpdate={isSeeded && !isLoading && !isError && !isSaving && canSave}
            onSelectionChange={editDraft}
            onClearAll={() => editDraft([])}
            onSelectAll={() => editDraft(locations.map((location) => location.id))}
            onUpdate={() => save(draft)}
            onCancel={() => onClose?.()}
        />
    );
};
